import { Alert, Box, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { LinkedBroadcast } from "@/api/broadcastV2Audiences";

import { useIsTextMessagingActive } from "@/api/siteCapabilities";
import { LinkedBroadcastsSaveConfirmModal } from "@/components/LinkedBroadcastsSaveConfirmModal";
import { RcsNotEnabledBanner } from "@/components/RcsNotEnabledBanner";
import { SmsNotEnabledBanner } from "@/components/SmsNotEnabledBanner";
import { AbTestToggle } from "@/features/BroadcastV2/components/AbTestToggle";
import { BroadcastAbSettingsSection } from "@/features/BroadcastV2/components/sections/BroadcastAbSettingsSection";
import { BroadcastCancelScheduleSection } from "@/features/BroadcastV2/components/sections/BroadcastCancelScheduleSection";
import { BroadcastConfirmSection } from "@/features/BroadcastV2/components/sections/BroadcastConfirmSection";
import { BroadcastScheduleSection } from "@/features/BroadcastV2/components/sections/BroadcastScheduleSection";
import { BroadcastSendDetailsSection } from "@/features/BroadcastV2/components/sections/BroadcastSendDetailsSection";
import { BroadcastSendWindowCaption } from "@/features/BroadcastV2/components/sections/BroadcastSendWindowCaption";
import { SuccessAnimation } from "@/features/BroadcastV2/components/shared/SuccessAnimation";
import { VariationTabs } from "@/features/BroadcastV2/components/VariationTabs";
import { broadcastV2ComposeFormSchema } from "@/features/BroadcastV2/createBroadcastPayload";
import {
  useIsMultiVariationEnabledForSite,
  useIsSmartSendEnabledForSite,
} from "@/features/BroadcastV2/flag";
import {
  useBroadcastComposeActions,
  useBroadcastComposeStore,
  useBroadcastComposeStoreApi,
  useBroadcastComposeStoreShallow,
  useIsAbDurationInvalid,
} from "@/features/BroadcastV2/store/broadcastComposeStore";
import { isCampaignLocked } from "@/features/BroadcastV2/store/broadcastComposeStoreFactory";
import {
  MAX_VARIATIONS,
  smsVariationFrameName,
} from "@/features/BroadcastV2/store/variationTypes";
import { type AudiencesBroadcastStatus } from "@/features/BroadcastV2/types";
import { formatSendWindowTime } from "@/features/Settings/tabs/text/sendWindow/service";
import { useMessageDraftSelectors } from "@/features/TextComposerV2/compose/useMessageDraftSelectors";
// Shared dayjs instance with the utc + timezone plugins already extended.
import dayjs from "@/utils/configuredDayjs";
import { getCurrentMinuteOfDayInTimezone } from "@/utils/getLocalQuietHours";

export interface BroadcastV2ComposeSmsFormProps {
  /** True on the edit page; gates readonly + linked-broadcast activate warning. */
  isEditMode?: boolean;
  broadcastStatus?: AudiencesBroadcastStatus;
  isScheduled?: boolean;
  /** Create-or-activate the broadcast. Owned by the page (reads the store). */
  onActivate: () => Promise<void>;
  onCancelSchedule?: () => Promise<void>;
  /** Registry-aware "add variant" (copies the active editor's content). */
  onAddVariation: () => void;
  timezone?: string;
  /** Send window bounds as minutes-of-day in the site's local time (null = unset). */
  sendWindowStart?: number | null;
  sendWindowEnd?: number | null;
  /** Edit-mode linked broadcasts, for the activate confirmation modal. */
  linkedBroadcasts?: LinkedBroadcast[];
  editBroadcastId?: string;
  /** Per-variation editor nodes (each in its own MessageDraftStoreContext). */
  renderMessageEditors: ReactNode;
  /** Page-level pipeline busy (frame/broadcast mutations). */
  submitBusy?: boolean;
}

/** Flattens zod issue paths into dotted keys (e.g. "scheduledAtIso"). */
function mapZodErrors(error: {
  issues: Array<{ path: (string | number)[]; message: string }>;
}): Record<string, string> {
  const result: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path.join(".");
    if (key && !result[key]) {
      result[key] = issue.message;
    }
  }
  return result;
}

/**
 * SMS compose/edit form. Channel-agnostic shape mirroring the email form: reads
 * shared scalars + variations + A/B config from the broadcast compose store, and
 * composes the same `sections/` building blocks. The message editors themselves
 * are injected by the page (one MessageDraftStore per variation), so this form
 * stays free of editor-store wiring.
 */
export function BroadcastV2ComposeSmsForm({
  isEditMode = false,
  broadcastStatus,
  isScheduled = false,
  onActivate,
  onCancelSchedule,
  onAddVariation,
  timezone: siteTimezone,
  sendWindowStart,
  sendWindowEnd,
  linkedBroadcasts,
  editBroadcastId,
  renderMessageEditors,
  submitBusy,
}: BroadcastV2ComposeSmsFormProps) {
  const { siteId = "" } = useParams({ strict: false });
  const navigate = useNavigate();
  // Reads the active variation's editor store (the page wraps us in its context).
  const { isAuthoringValid, isCtaValid, isSuggestionsValid, derivedOutput } =
    useMessageDraftSelectors();

  const isDraft =
    !broadcastStatus || broadcastStatus === "BROADCAST_STATUS_DRAFT";
  const isActive = broadcastStatus === "BROADCAST_STATUS_ACTIVE";
  const isReadonly = isEditMode && !isDraft;
  const canCancelScheduledBroadcast =
    isActive && isScheduled && Boolean(onCancelSchedule);
  const multiVariationAllowed = useIsMultiVariationEnabledForSite(siteId);
  const maxVariations = multiVariationAllowed ? MAX_VARIATIONS : 2;
  const smartSendAllowed = useIsSmartSendEnabledForSite(siteId);
  // Drafts can still be created/saved, but a site without text messaging
  // activated (Infobip-registered) cannot schedule/send a text broadcast.
  const isTextActive = useIsTextMessagingActive(siteId);

  const storeApi = useBroadcastComposeStoreApi();
  const actions = useBroadcastComposeActions();

  const name = useBroadcastComposeStore((s) => s.name);
  const nameError = useBroadcastComposeStore((s) => s.fieldErrors.name ?? "");
  const audienceOptions = useBroadcastComposeStore((s) => s.audienceOptions);
  const excludedAudienceOptions = useBroadcastComposeStoreShallow(
    (s) => s.excludedAudienceOptions,
  );
  const audienceError = useBroadcastComposeStore(
    (s) => s.fieldErrors.audienceIds ?? "",
  );
  const sendMode = useBroadcastComposeStore((s) => s.sendMode);
  const smartSend = useBroadcastComposeStore((s) => s.smartSend);
  const reachOptimization = useBroadcastComposeStore(
    (s) => s.reachOptimization,
  );
  const reachOptimizationPercentage = useBroadcastComposeStore(
    (s) => s.reachOptimizationPercentage,
  );
  const enforceFrequencyCap = useBroadcastComposeStore(
    (s) => s.enforceFrequencyCap,
  );
  const scheduledAtIso = useBroadcastComposeStore((s) => s.scheduledAtIso);
  const scheduleWindow = useBroadcastComposeStoreShallow(
    (s) => s.scheduleWindow,
  );
  const calendarOwned = useBroadcastComposeStore((s) => s.calendarOwned);
  const campaignLocked = isCampaignLocked(calendarOwned, scheduleWindow);
  const scheduledAtError = useBroadcastComposeStore(
    (s) => s.fieldErrors.scheduledAtIso ?? "",
  );
  const confirmSend = useBroadcastComposeStore((s) => s.confirmSend);
  const confirmSendError = useBroadcastComposeStore(
    (s) => s.fieldErrors.confirmSend ?? "",
  );
  const variations = useBroadcastComposeStoreShallow((s) => s.variations);
  const activeVariationIndex = useBroadcastComposeStore(
    (s) => s.activeVariationIndex,
  );
  const abTest = useBroadcastComposeStoreShallow((s) => s.abTest);
  const abEnabled = variations.length > 1;

  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkedActivateModalOpen, setLinkedActivateModalOpen] = useState(false);

  const otherLinkedBroadcastsForActivateWarning = useMemo(() => {
    if (!linkedBroadcasts?.length || !editBroadcastId) return [];
    return linkedBroadcasts.filter((b) => b.id !== editBroadcastId);
  }, [linkedBroadcasts, editBroadcastId]);

  // --- Send window ---
  // The send window is the daily local-time span during which texts are
  // delivered; anything scheduled outside it is held until the window reopens.
  // Bounds are minutes-of-day in the site's own timezone (start < end, no
  // midnight wrap — enforced by Settings), so we compare against the site's
  // wall-clock time directly, no UTC conversion.
  const [nowTick, setNowTick] = useState(0);
  const currentTimezone =
    siteTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const sendWindow = useMemo(() => {
    if (
      sendWindowStart == null ||
      sendWindowEnd == null ||
      sendWindowStart >= sendWindowEnd
    ) {
      return null;
    }
    return { start: sendWindowStart, end: sendWindowEnd };
  }, [sendWindowStart, sendWindowEnd]);
  const sendWindowOpensLabel = sendWindow
    ? formatSendWindowTime(sendWindow.start)
    : null;
  const isNowOutsideSendWindow = useMemo(() => {
    void nowTick;
    if (!sendWindow) return false;
    const nowMinute = getCurrentMinuteOfDayInTimezone(currentTimezone);
    return nowMinute < sendWindow.start || nowMinute >= sendWindow.end;
  }, [currentTimezone, sendWindow, nowTick]);

  useEffect(() => {
    const id = window.setInterval(() => setNowTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Keep the store's primaryChannel in sync with the active editor's channel.
  useEffect(() => {
    actions.setPrimaryChannel(derivedOutput.primaryChannel);
  }, [derivedOutput.primaryChannel, actions]);

  // Out-of-window times stay selectable; we warn rather than disable. The picker
  // interprets the value in the store timezone, so compare on that same zone's
  // minute-of-day.
  const isScheduledOutsideSendWindow = useMemo(() => {
    if (sendMode !== "schedule" || !scheduledAtIso || !sendWindow) {
      return false;
    }
    const picked = dayjs(scheduledAtIso).tz(currentTimezone);
    const minute = picked.hour() * 60 + picked.minute();
    return minute < sendWindow.start || minute >= sendWindow.end;
  }, [sendMode, scheduledAtIso, sendWindow, currentTimezone]);

  const handleSuccessAnimationComplete = useCallback(() => {
    setShowSuccessAnimation(false);
    void navigate({
      to: "/dragon-tiles/$siteId/broadcast/list",
      params: { siteId },
    });
  }, [navigate, siteId]);

  // A/B is derived from the variation count. Turning it off parks the extra
  // variations (preserved in-session, but not sent/saved) so an accidental
  // toggle doesn't wipe work; turning it back on restores them, or adds the
  // first extra variation (registry-aware, copying the active editor) if
  // there's nothing parked.
  const handleAbToggle = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        actions.setSmartSend(false);
        if (storeApi.getState().stashedVariations.length > 0) {
          actions.restoreStashedVariations();
        } else {
          onAddVariation();
        }
        // Default to viewing Variation A.
        actions.setActiveVariation(0);
      } else {
        actions.stashExtraVariations();
      }
    },
    [onAddVariation, actions, storeApi],
  );

  const runActivate = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onActivate();
      setShowSuccessAnimation(true);
    } catch {
      // Errors are surfaced by the page's mutation handlers.
    } finally {
      setIsSubmitting(false);
    }
  }, [onActivate]);

  function handleSubmit() {
    if (!isCtaValid) {
      enqueueSnackbar("Fix CTA button errors before saving", {
        variant: "error",
      });
      return;
    }
    if (!isSuggestionsValid) {
      enqueueSnackbar("Fix suggested action errors before saving", {
        variant: "error",
      });
      return;
    }
    const state = storeApi.getState();
    const parsed = broadcastV2ComposeFormSchema.safeParse({
      name: state.name,
      audienceIds: state.audienceIds,
      sendMode: state.sendMode,
      scheduledAtIso: state.scheduledAtIso,
      confirmSend: state.confirmSend,
      primaryChannel: state.primaryChannel,
    });
    if (!parsed.success) {
      state.actions.setFieldErrors(mapZodErrors(parsed.error));
      enqueueSnackbar(parsed.error.issues[0]?.message ?? "Check the form", {
        variant: "error",
      });
      return;
    }
    if (isEditMode && otherLinkedBroadcastsForActivateWarning.length > 0) {
      setLinkedActivateModalOpen(true);
      return;
    }
    void runActivate();
  }

  const handleConfirmLinkedActivate = useCallback(() => {
    setLinkedActivateModalOpen(false);
    void runActivate();
  }, [runActivate]);

  const isConfirmTyped = confirmSend === "CONFIRM";
  const isBusy = isSubmitting || Boolean(submitBusy);
  const abDurationInvalid = useIsAbDurationInvalid();

  return (
    <>
      <Stack
        component="form"
        gap={4}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
        sx={{ flex: 1, minHeight: 0 }}
      >
        <SmsNotEnabledBanner siteId={siteId} />
        <RcsNotEnabledBanner siteId={siteId} />
        <BroadcastSendDetailsSection
          siteId={siteId}
          // Intentionally constant across SMS/MMS/RCS: `/reach` only distinguishes
          // email-eligible vs text-eligible, and all text channels gate on SMS.
          medium="MEDIUM_TYPE_SMS"
          name={name}
          onNameChange={actions.setName}
          nameError={nameError || undefined}
          audienceOptions={audienceOptions}
          onAudiencesChange={(opts) =>
            actions.setAudiences(
              opts.map((o) => o.id),
              opts,
            )
          }
          audienceError={audienceError || undefined}
          excludedAudienceOptions={excludedAudienceOptions}
          onExcludedAudiencesChange={(opts) =>
            actions.setExcludedAudiences(
              opts.map((o) => o.id),
              opts,
            )
          }
          disabled={isReadonly}
          reachOptimization={reachOptimization}
          onReachOptimizationChange={actions.setReachOptimization}
          reachOptimizationPercentage={reachOptimizationPercentage}
          onReachOptimizationPercentageChange={
            actions.setReachOptimizationPercentage
          }
          showEnforceFrequencyCap
          enforceFrequencyCap={enforceFrequencyCap}
          onEnforceFrequencyCapChange={actions.setEnforceFrequencyCap}
        />

        {/* Message content */}
        <Stack
          gap={4}
          sx={{
            backgroundColor: (theme) =>
              theme.palette.background["paper-elevation-0"],
            borderRadius: (theme) => `${theme.shape.borderRadiusMd}px`,
            p: 6,
          }}
        >
          <Typography variant="subtitle1">Message</Typography>
          <AbTestToggle
            checked={abEnabled}
            onChange={handleAbToggle}
            disabled={isReadonly}
          />
          {/* With A/B on, the variation tabs sit atop a connected panel (its
              top-left squared so it joins the active tab). With A/B off there
              are no tabs, so the editor — which already provides its own
              surface — renders directly, dropping the redundant wrapper. */}
          {abEnabled ? (
            <Box>
              <VariationTabs
                items={variations.map((v, i) => ({
                  id: v.id,
                  label: smsVariationFrameName(name, i, variations.length),
                  removable: i > 0,
                }))}
                activeIndex={activeVariationIndex}
                onSelect={(index) => actions.setActiveVariation(index)}
                onAdd={
                  isReadonly || variations.length >= maxVariations
                    ? undefined
                    : onAddVariation
                }
                onRemove={(index) => actions.removeVariation(index)}
                disabled={isReadonly}
              />
              <Stack
                gap={4}
                sx={(theme) => ({
                  backgroundColor:
                    theme.palette.background["paper-elevation-1"],
                  borderRadius: `${theme.shape.borderRadiusMd}px`,
                  borderTopLeftRadius: 0,
                  p: 6,
                })}
              >
                {renderMessageEditors}
              </Stack>
            </Box>
          ) : (
            renderMessageEditors
          )}
          {abEnabled && (
            <BroadcastAbSettingsSection
              winningMetric={abTest.winningMetric}
              onWinningMetricChange={actions.setWinningMetric}
              testSizePercent={abTest.testSizePercent}
              onTestSizePercentChange={actions.setTestSizePercent}
              testDurationValue={abTest.testDurationValue}
              onTestDurationValueChange={actions.setTestDurationValue}
              testDurationUnit={abTest.testDurationUnit}
              onTestDurationUnitChange={actions.setTestDurationUnit}
              disabled={isReadonly}
              hideOpenRate
              tileCount={variations.length}
            />
          )}
        </Stack>

        <BroadcastScheduleSection
          sendMode={sendMode}
          scheduleWindow={scheduleWindow}
          calendarOwned={calendarOwned}
          onSendModeChange={actions.setSendMode}
          scheduledAtIso={scheduledAtIso}
          onScheduledAtChange={actions.setScheduledAtIso}
          scheduleError={scheduledAtError || undefined}
          disabled={isReadonly}
          caption={<BroadcastSendWindowCaption siteId={siteId} />}
          instantNotice={
            isNowOutsideSendWindow && sendMode === "instant" ? (
              <Alert severity="warning">
                Your send window is closed right now. This broadcast will be
                held and delivered when it next opens at {sendWindowOpensLabel}{" "}
                in each recipient&apos;s local time.
              </Alert>
            ) : undefined
          }
          notice={
            isScheduledOutsideSendWindow ? (
              <Alert severity="warning">
                The selected time is outside your send window. This message will
                be held and delivered when the window next opens at{" "}
                {sendWindowOpensLabel} in each recipient&apos;s local time.
              </Alert>
            ) : undefined
          }
          timezone={siteTimezone}
          showSmartSend={smartSendAllowed}
          smartSend={smartSend}
          onSmartSendChange={actions.setSmartSend}
          smartSendDisabledReason={
            abEnabled ? "Smart Send isn't available for A/B tests." : undefined
          }
        />

        {!isReadonly && (
          <BroadcastConfirmSection
            confirmValue={confirmSend}
            onConfirmChange={actions.setConfirmSend}
            confirmError={confirmSendError || undefined}
            onSubmit={() => handleSubmit()}
            // Campaign broadcasts are activated by scheduling the campaign,
            // never sent directly from the composer.
            disabled={campaignLocked}
            submitDisabled={
              isBusy ||
              !isConfirmTyped ||
              !isAuthoringValid ||
              !isTextActive ||
              abDurationInvalid
            }
            submitBusy={isBusy}
            submitLocked={!isTextActive}
            lockedTooltip={<SmsNotEnabledBanner siteId={siteId} />}
            submitDisabledReason={
              campaignLocked
                ? "Campaign broadcasts are sent by scheduling the campaign"
                : !isCtaValid
                  ? "Fix CTA button errors before saving"
                  : !isSuggestionsValid
                    ? "Fix suggested action errors before saving"
                    : abDurationInvalid
                      ? "Test duration must be at least 4 hours"
                      : undefined
            }
          />
        )}

        {canCancelScheduledBroadcast && (
          <BroadcastCancelScheduleSection
            onCancelSchedule={() => void onCancelSchedule?.()}
          />
        )}
      </Stack>

      <LinkedBroadcastsSaveConfirmModal
        open={linkedActivateModalOpen}
        broadcasts={linkedBroadcasts ?? []}
        isSaving={isSubmitting}
        onCancel={() => setLinkedActivateModalOpen(false)}
        onConfirm={handleConfirmLinkedActivate}
      />
      <SuccessAnimation
        isVisible={showSuccessAnimation}
        onComplete={handleSuccessAnimationComplete}
      />
    </>
  );
}
